// Validation helpers (see FORMS.md). A schema maps a field name to its label
// and an ordered list of rules; the first failing rule supplies the message.
//
//   const schema = {
//     title: { label: 'Title', rules: [required(), maxLength(120)] },
//     email: { label: 'Email', rules: [required(), email()] },
//   };
//   const errors = validateForm(values, schema);
//   if (hasErrors(errors)) { setErrors(errors); focusFirstError(errors, order); }
//
// A rule is (value, { label, values }) => message | undefined. Messages are
// short sentences without a trailing full stop, so errors read the same
// everywhere: "Title is required", "Enter a valid email address".

export const isEmpty = value =>
  value === null ||
  value === undefined ||
  (typeof value === 'string' && value.trim() === '') ||
  (Array.isArray(value) && value.length === 0);

export const required =
  message =>
  (value, { label }) =>
    isEmpty(value) ? message || `${label} is required` : undefined;

export const minLength =
  (min, message) =>
  (value, { label }) =>
    !isEmpty(value) && String(value).trim().length < min
      ? message || `${label} must be at least ${min} characters`
      : undefined;

export const maxLength =
  (max, message) =>
  (value, { label }) =>
    !isEmpty(value) && String(value).length > max
      ? message || `${label} must be at most ${max} characters`
      : undefined;

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
export const email = message => value =>
  !isEmpty(value) && !EMAIL.test(String(value).trim())
    ? message || 'Enter a valid email address'
    : undefined;

export const pattern =
  (regex, message) =>
  (value, { label }) =>
    !isEmpty(value) && !regex.test(String(value))
      ? message || `${label} is not in the expected format`
      : undefined;

export const numberBetween =
  (min, max, message) =>
  (value, { label }) => {
    if (isEmpty(value)) return undefined;
    const n = Number(value);
    if (Number.isNaN(n)) return `${label} must be a number`;
    if (n < min || n > max) {
      return message || `${label} must be between ${min} and ${max}`;
    }
    return undefined;
  };

// The date in `otherField` must not be later than this one (e.g. an end date
// that must not come before the start date).
export const notBefore =
  (otherField, otherLabel, message) =>
  (value, { label, values }) => {
    const other = values[otherField];
    if (isEmpty(value) || isEmpty(other)) return undefined;
    return new Date(value) < new Date(other)
      ? message || `${label} cannot be before ${otherLabel}`
      : undefined;
  };

// Numeric counterpart of notBefore: this value must not be below the number
// in `minField` (e.g. a "max" box under a "min" box). Empty values pass.
export const notBelow =
  (minField, minLabel, message) =>
  (value, { label, values }) => {
    const min = values[minField];
    if (isEmpty(value) || isEmpty(min)) return undefined;
    return Number(value) < Number(min)
      ? message || `${label} cannot be less than ${minLabel}`
      : undefined;
  };

// A rule that only applies when `when(values)` is true, for fields that are
// required only under some condition.
export const onlyWhen = (when, rule) => (value, ctx) =>
  when(ctx.values) ? rule(value, ctx) : undefined;

export const validateForm = (values, schema) => {
  const errors = {};
  Object.entries(schema).forEach(([name, { label, rules = [] }]) => {
    for (const rule of rules) {
      const message = rule(values[name], { label: label || name, values });
      if (message) {
        errors[name] = message;
        break;
      }
    }
  });
  return errors;
};

export const hasErrors = errors => Object.values(errors).some(Boolean);

// Scroll to a field by name and put the cursor in it.
export const focusField = name => {
  if (typeof document === 'undefined') return;
  const wrapper = document.querySelector(`[data-field="${name}"]`);
  if (!wrapper) return;
  const reduceMotion = window.matchMedia?.(
    '(prefers-reduced-motion: reduce)'
  ).matches;
  wrapper.scrollIntoView({
    behavior: reduceMotion ? 'auto' : 'smooth',
    block: 'center',
  });
  const target = wrapper.querySelector(
    'input:not([type="hidden"]):not(:disabled), select:not(:disabled), textarea:not(:disabled)'
  );
  target?.focus({ preventScroll: true });
};

// `order` is the field order on screen; the first one with an error gets focus.
export const focusFirstError = (errors, order = Object.keys(errors)) => {
  const first = order.find(name => errors[name]);
  if (first) focusField(first);
};
